import { Button, Row, Col, Tag, Typography, Progress } from 'antd'
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons'
import ChatLayout from '@/Layout/ChatLayout'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { ManagerDetail } from '@/types'
import './index.scss'
import AICreate from '../components/AIManagers'
import { getManagersDetail, setManagerDefaultPool } from '@/api'
import { useEffect, useMemo, useState } from 'react'
import { colors, groupColors } from '../../static'
import ContactPool from '../../../components/ContactPool'
import { useFeedback } from '@/utils/antdApp'

const { Text } = Typography

type MetricItem = {
    label: string
    value: 'annual_return' | 'sharpe_ratio' | 'max_drawdown' | 'portfolios_count' | 'total_assets' | 'win_rate'
    tone?: 'positive' | 'negative'
    unit?: string
}

const metrics: MetricItem[] = [
    { label: '年化收益率', value: 'annual_return', tone: 'positive' , unit: '%'},
    { label: '夏普比率', value: 'sharpe_ratio' },
    { label: '最大回撤', value: 'max_drawdown', tone: 'negative' , unit: '%'},
    { label: '管理组合数', value: 'portfolios_count' , unit: '个'},
    { label: '管理规模', value: 'total_assets' , unit: '万'},
    { label: '历史胜率', value: 'win_rate', tone: 'positive' , unit: '%'},
]

export default function ManagerDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [searchParams] = useSearchParams()
    const idx = Number(searchParams.get('idx')?.trim() ?? 0)
    const index = Number(searchParams.get('index')?.trim() ?? 0)
    const [manager, setManager] = useState<ManagerDetail | null>(null)
    const [riskLevelColor, setRiskLevelColor] = useState<string>('default')
    const [visible, setVisible] = useState(false)
    const { message } = useFeedback()

    const load = () => {
        getManagersDetail(Number(id)).then(
            (data) => {
                setManager(data)
                getRiskLevelColor(data.risk_level)
            }
        ).catch((err) => {
            console.error(err)
        })
    }

    const getRiskLevelColor = (riskLevel?: string) => {
        console.log(riskLevel)
        let color = 'accent'
        if (!riskLevel) {
            color = 'accent'
        }
        if(riskLevel?.includes('低')) {
            color = 'success'
        }else if(riskLevel?.includes('中')) {
            color = 'warning'
        }else if(riskLevel?.includes('高')) {
            color = 'error'
        }
        setRiskLevelColor( `is-${color}` )
    }

    useEffect(() => {
        load()
    }, [id])

    const avatarBackground = useMemo(
        () => `color-mix(in srgb, ${groupColors[index % groupColors.length]} 50%, ${colors[idx % colors.length]} 50%)`,
        [index, idx],
    )

    const onContactPool = (poolId: number | null) => {
        setManagerDefaultPool(Number(id), poolId).then(() => {
            message.success('设置成功')
            setVisible(false)
            load()
        }).catch(() => message.error('设置失败'))
    }

    return (
        <ChatLayout
            className='manager-detail-page'
            headerClassName="manager-detail-page__header"
            contentClassName="manager-detail-page__form-column"
            siderClassName="manager-detail-page__ai-panel"
            header={
                <>
                    <div className="manager-detail-page__header-left">
                        <Button
                            type="text"
                            className="manager-detail-page__back"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/managers')}
                        >
                            全部经理
                        </Button>
                        <span>/</span>
                        <Text className="manager-detail-page__crumb">{manager?.nickname ?? '--'}</Text>
                        <Tag className='text-10 is-accent'>{manager?.style ?? '--'}</Tag>
                        <Tag className='text-10 is-success' style={{margin: '0 4px'}}>{manager?.active_strategy_version?.version_no ?? '--'} 已激活</Tag>
                        {
                            (manager?.pending_strategy_versions_count ?? 0) > 0 && <Tag className='text-10 is-warning' style={{margin: '0 4px'}}>待审批</Tag>
                        }
                    </div>
                    <div className="manager-detail-page__actions">
                        <Button onClick={() => navigate(`/versionManage?manager_id=${id}`)}>📋 版本管理</Button>
                        <Button onClick={() => navigate(`/managers/operate/${id}`)} icon={<EditOutlined />} style={{ color: '#94a3b8' }} >修改经理</Button>
                        <Button type="primary" onClick={() => navigate(`/asset-mgmt/create?manager_id=${id}`)}>
                            <Text style={{ fontSize: 16 }}>+</Text> 创建资产组合
                        </Button>
                    </div>
                </>
            }
            content={
                <div className="manager-detail-page__content">
                    <section className="manager-detail-page__hero">
                        <div className="manager-detail-page__avatar" 
                            style={{ backgroundColor: avatarBackground }}
                        >
                            {manager?.initials?.toUpperCase() ?? '--'}
                        </div>
                        <div className="manager-detail-page__hero-main">
                            <div className="manager-detail-page__title-row">
                                <h1 className="manager-detail-page__title">{manager?.nickname ?? '--'}</h1>
                                <div className="manager-detail-page__tags">
                                    <Tag className='text-10 is-accent'>{manager?.style ?? '--'}</Tag>
                                    <Tag className={`text-10 ${riskLevelColor}`} style={{margin: '0 4px'}}>{manager?.risk_level ?? '--'}</Tag>
                                    <span className='text-10'>进化 {manager?.evolution_count ?? '--'} 次</span>
                                </div>
                            </div>
                            <p className="manager-detail-page__desc">
                                {manager?.investment_brief ?? '--'}
                            </p>
                        </div>
                    </section>

                    <section className="manager-detail-page__section">
                        <div className="manager-detail-page__section-title">核心绩效</div>
                        <Row gutter={[16, 16]}>
                            {metrics.map((item) => (
                                <Col key={item.label} xs={24} md={12} xl={8}>
                                    <div className="manager-detail-page__metric-card">
                                        <div className="manager-detail-page__metric-label">{item.label}</div>
                                        <div className={`manager-detail-page__metric-value ${item.tone ?? ''}`}>
                                            {manager?.[item.value] ?? '--'} {item.unit ?? ''}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section className="manager-detail-page__section">
                        <div className="manager-detail-page__section-title">当前激活策略</div>
                        <div className="manager-detail-page__strategy-card">
                            <div className="manager-detail-page__strategy-label">{manager?.active_strategy_version?.version_no ?? '--'} · {manager?.active_strategy_version?.name ?? '--'}</div>
                            <div className="manager-detail-page__strategy-value">{manager?.active_strategy_version?.strategy_snapshot ?? '--'}</div>
                        </div>
                        <div className="manager-detail-page__subsection">
                            <div className="manager-detail-page__subsection-title">关注板块</div>
                            <div className="manager-detail-page__chip-list">
                                {manager?.focus_sectors?.map((item) => (
                                    <Tag className="label-tag" key={item}>{item}</Tag>
                                ))}
                            </div>
                        </div>

                        <div className="manager-detail-page__subsection">
                            <div className="manager-detail-page__subsection-title">风险约束</div>
                            <div className="manager-detail-page__constraint-list">
                                {manager?.constraints?.map((item) => (
                                    <div key={item} className="manager-detail-page__constraint-item">
                                        <span className="manager-detail-page__constraint-dot" />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="manager-detail-page__section">
                        <div className="manager-detail-page__section-title">
                            分析维度配置
                            <span className='text-11 colo-3'>Research Agent 数据权重</span>
                        </div>
                        <div className="manager-detail-page__dimension-list">
                            {manager?.active_strategy_version?.dimension_scores?.dimensions?.map((item) => (
                                <div key={item.key} className={`manager-detail-page__dimension-list-item ${!item.weight ? 'is-active' : ''}`}>
                                    <div className="manager-detail-page__dimension-item">
                                        <div className="manager-detail-page__dimension-label color-2 text-12">{item.name}</div>
                                        <Progress  percent={item.weight ?? 0} size="small" showInfo={false} strokeColor={item.weight ? 'var(--accent)' : 'var(--border)'} />
                                        <div className="text-10 color-2 manager-detail-page__dimension-value">{item.weight ? item.weight : '未启用'}</div>
                                    </div>
                                    <div className="text-10 color-3 manager-detail-page__dimension-basis">{item.basis}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="manager-detail-page__section">
                        <div className="manager-detail-page__section-title">
                            AGENT 进化记录 <span>累计 {manager?.evolution_count ?? '--'} 次进化</span>
                        </div>
                        <div className="manager-detail-page__timeline">
                            {manager?.evolutions?.map((item) => (
                                <div key={item.id} className="manager-detail-page__timeline-item">
                                    <div className="manager-detail-page__timeline-left" >
                                        <div className="manager-detail-page__timeline-dot" />
                                        <div className="manager-detail-page__timeline-vline" />
                                    </div>
                                    <div className="manager-detail-page__timeline-main">
                                        <div className="manager-detail-page__timeline-meta">
                                            <Tag color="blue" style={{ fontSize: '10px' }}>{item.type}</Tag>
                                            <span style={{ fontSize: '11px' }}>{item.date}</span>
                                        </div>
                                        <div className="manager-detail-page__timeline-content">{item.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    <section className="manager-detail-page__section">
                        <div className="manager-detail-page__section-title">
                            默认股票池<span>选股宇宙范围</span>
                        </div>
                        <div className="manager-detail-page__timeline">
                            { manager?.default_stock_pool_id && 
                                <Tag style={{ width: 'fit-content',padding: '6px 10px',textAlign: 'center',marginBottom: 10 }} color="blue">
                                    {manager?.default_stock_pool_name}
                                </Tag>
                            }
                            <Button className='create-portfolio-button' onClick={() => setVisible(true)}>
                                {manager?.default_stock_pool_id ? '修改股票池' : '+ 关联股票池'}
                            </Button>
                        </div>
                    </section>

                    <section className="manager-detail-page__section">
                        <div className="manager-detail-page__section-title">
                            管理的资产组合
                        </div>
                        <div className="manager-detail-page__timeline">
                            <Button className='create-portfolio-button' onClick={() => navigate(`/asset-mgmt/create?manager_id=${id}`)}>
                                + 新建组合
                            </Button>
                        </div>
                    </section>
                    <ContactPool open={visible} onCancel={() => setVisible(false)} defaultStockPoolId={manager?.default_stock_pool_id} onContactPool={onContactPool} />
                </div>
            }
            sider={
                <AICreate formData={manager ?? {}} />
            }
        />
    )
}
